import type { AriaAttributes, ReactNode } from 'react';
import { cloneElement, isValidElement, useId } from 'react';

import classNames from 'classnames';

import styles from './FormControl.module.css';

import type { PolitenessSetting } from '../StatusMessage';
import { StatusMessage } from '../StatusMessage';
import { Text } from '../Text';

export interface FormControlProps extends AriaAttributes {
  id?: string;
  className?: string;
  /** The interactive element to provide messages for, */
  children?: ReactNode;
  /** The message to provide to the user */
  message?: ReactNode;
  /** Whether to toggle the invalid state */
  invalid?: boolean;
  /** Politeness setting used for aria-live regions. */
  politeness?: PolitenessSetting;
  /** @deprecated Use `politeness` instead. */
  ['aria-live']?: PolitenessSetting;
}

/**
 * Display a message or a validation message below a form control.
 *
 * @remarks
 * This component is planned to be deprecated. See
 * {@link https://app.shortcut.com/farmersdog/story/91105 | SC-91105}
 * @see https://corgi-x.tfd.engineering/components/formcontrol
 */
export function FormControl(props: FormControlProps) {
  const reactId = useId();
  const id = props.id || reactId;

  const className = classNames(styles.container, props.className);

  const children = isValidElement<{
    invalid: boolean;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  }>(props.children)
    ? cloneElement(props.children, {
        invalid: props.invalid,
        ['aria-describedby']: props['aria-describedby'] || id,
        ['aria-invalid']: props.invalid,
      })
    : props.children;

  const visuallyHidden = !props.message;
  return (
    <div className={className}>
      {children}
      <Text color="Charcoal2">
        <StatusMessage
          id={id}
          politeness={props.politeness ?? 'polite'}
          visuallyHidden={visuallyHidden}
          invalid={props.invalid}
          className={!visuallyHidden ? styles.message : undefined}
        >
          {props.message}
        </StatusMessage>
      </Text>
    </div>
  );
}
